import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  Course,
  CourseAttempt,
  CourseStatusAttemptEnum,
} from '@/client/courses';
import { ScormVersion } from '@/client/learner-course';
import { User } from '@/client/users';
import { usePosthogTracking } from '@/hooks/usePosthogTracking';
import { useToast } from '@/hooks/useToast';
import { attemptLog } from '@/utils/helpers';
import {
  buildPostHogGlobalProperties,
  PostHogCustomEvent,
  trackableAccountIds,
} from '@/utils/posthog';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { scormFactory } from './scorm.factory';

type ScormContextProps = {
  values: Record<string, any>;
  errors: Record<string, any>;
};

const ScormContext = createContext<ScormContextProps>({
  values: {},
  errors: {},
});

type ScormProviderProps = {
  children: React.ReactNode;
  version: ScormVersion;
  attempt?: CourseAttempt;
  course: Course;
  user: User;
  setStatus?: (status?: CourseStatusAttemptEnum) => void;
  setScore?: (score?: number) => void;
  setIsSubmittingStatus?: (isSubmittingStatus: boolean) => void;
  setIsBellowThreshold?: (isBellowThreshold: boolean) => void;
  isPreview: boolean;
  isLastModule?: boolean;
  setNextModule?: () => void;
};

export const ScormProvider: React.FC<ScormProviderProps> = ({
  children,
  version,
  attempt,
  course,
  user,
  setStatus,
  setScore,
  setIsSubmittingStatus,
  setIsBellowThreshold,
  isPreview,
  isLastModule,
  setNextModule,
}) => {
  const toast = useToast();
  const { trackPostHogEvent } = usePosthogTracking();
  const [scormValues, setScormValues] = useState<Record<string, any>>(
    attempt?.payload ?? {},
  );
  const [scormErrors, setScormErrors] = useState<Record<string, any>>({});
  const hasSetSubmittingStatus = useRef<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true);
  const hasCompleted = useRef<boolean>(false);

  useEffect(() => {
    hasCompleted.current = false;
  }, [attempt?.id]);

  useEffect(() => {
    if (isPreview) return;

    const handleBeforeUnload = (event: any) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, isPreview]);

  const saveScormValues = async (
    payload: any,
    status: CourseStatusAttemptEnum,
    timeSpent: number,
    score: number | null,
  ) => {
    if (!attempt?.course?.id || !attempt.id) {
      return;
    }

    const isTrackableAccount = trackableAccountIds.includes(user.account.id);
    const courseMinimumScore = user?.account?.courseMinimumScore ?? 0;
    const isBellowThreshold =
      score !== null &&
      status === CourseStatusAttemptEnum.COMPLETED &&
      score < courseMinimumScore &&
      !course.exemptFromMinimumRequirementScore;
    const statusModified = isBellowThreshold
      ? CourseStatusAttemptEnum.IN_PROGRESS
      : status;
    const isCompleted = status === CourseStatusAttemptEnum.COMPLETED;

    if (
      statusModified === CourseStatusAttemptEnum.COMPLETED &&
      hasCompleted.current
    ) {
      return;
    }

    if (statusModified === CourseStatusAttemptEnum.COMPLETED) {
      hasCompleted.current = true;

      if (score === null) {
        void attemptLog(
          course,
          attempt.id,
          'SCORM score.raw is not set by the content package — skipping minimum score check',
        );
      }
    }

    if (isTrackableAccount) {
      // Track course commits without waiting to be stored in BE
      trackPostHogEvent(
        isCompleted
          ? PostHogCustomEvent.COURSE_COMPLETED
          : PostHogCustomEvent.COURSE_COMMIT,
        {
          attemptId: attempt?.id,
          score: score ?? 0,
          status,
          ...buildPostHogGlobalProperties(user.account, user),
        },
      );
    }

    try {
      if (
        status === CourseStatusAttemptEnum.COMPLETED &&
        attempt.status !== CourseStatusAttemptEnum.COMPLETED &&
        !hasSetSubmittingStatus.current
      ) {
        setIsSubmittingStatus?.(true);
        hasSetSubmittingStatus.current = true;
      }

      setHasUnsavedChanges(true);

      await client.learnerCourses.updateCourseAttempt({
        courseId: attempt.course.id,
        attemptId: attempt.id,
        payload: {
          payload,
          status: statusModified,
          timeSpent,
          ...(score !== null && { score }),
        },
      });

      // Track course completed request success
      if (isTrackableAccount && isCompleted) {
        trackPostHogEvent(PostHogCustomEvent.COURSE_COMPLETED_REQUEST_SUCCESS, {
          ...buildPostHogGlobalProperties(user.account, user),
          attemptId: attempt?.id,
          score: score ?? 0,
          status,
        });
      }

      setStatus?.(statusModified);
      if (score !== null) {
        setScore?.(score);
      }
    } catch (error) {
      hasCompleted.current = false;
      handleAxiosError(error as Error | AxiosError, toast);
    } finally {
      if (setIsBellowThreshold && isBellowThreshold) {
        setIsBellowThreshold(isBellowThreshold);
      }

      if (
        !isBellowThreshold &&
        status === CourseStatusAttemptEnum.COMPLETED &&
        !isLastModule
      ) {
        setNextModule?.();
      }

      setTimeout(() => {
        setIsSubmittingStatus?.(false);
        setHasUnsavedChanges(false);
      }, 200);
    }
  };

  const saveScormValuesRef = useRef(saveScormValues);
  saveScormValuesRef.current = saveScormValues;
  const setStatusRef = useRef(setStatus);
  setStatusRef.current = setStatus;
  const setIsSubmittingStatusRef = useRef(setIsSubmittingStatus);
  setIsSubmittingStatusRef.current = setIsSubmittingStatus;
  const isLastModuleRef = useRef(isLastModule);
  isLastModuleRef.current = isLastModule;
  const setNextModuleRef = useRef(setNextModule);
  setNextModuleRef.current = setNextModule;
  const isPreviewRef = useRef(isPreview);
  isPreviewRef.current = isPreview;

  useEffect(() => {
    const { destroy } = scormFactory(user, version, {
      onSetValue: (element, value, api) => {
        setScormValues(api);
      },
      onCommit: (
        api: any,
        status: CourseStatusAttemptEnum,
        timeSpent: number,
        score: number | null,
      ) => {
        if (!isPreviewRef.current) {
          saveScormValuesRef.current(api?.cmi, status, timeSpent, score);
        } else {
          setStatusRef.current?.(status);

          if (
            status === CourseStatusAttemptEnum.COMPLETED &&
            !isLastModuleRef.current
          ) {
            setTimeout(() => {
              setIsSubmittingStatusRef.current?.(false);
            }, 200);

            setNextModuleRef.current?.();
          }
        }
      },
      onInit: (api) => {
        if (!isPreviewRef.current) {
          api?.loadFromJSON(attempt?.payload ?? {});
        }
      },
      onError: (error) => {
        setScormErrors(error);
        toast?.error(t('error'), error);
      },
    });

    return () => {
      destroy();
    };
  }, [version, attempt?.id, user.id]);

  return (
    <ScormContext.Provider value={{ values: scormValues, errors: scormErrors }}>
      {children}
    </ScormContext.Provider>
  );
};

// Custom hook to use the SCORM context
export const useScorm = () => useContext(ScormContext);
